import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import { Moment } from 'moment';
import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { getFilteredFiles } from '../../../helpers/getFilteredFiles';
import { ObligationFile } from '../../../operations/file/types';
import { FilesModalProps } from '..';
import { FilesModal } from '../FilesModal';
import {
  CalendarCellStyled,
  CalendarStyled,
  CellTagStyled,
  FileBadge,
} from '../styles';

const CalendarWrapper = styled.div`
  position: relative;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 8px;
`;

const MonthNavigator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const MonthYearLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  min-width: 120px;
  text-align: center;
  text-transform: capitalize;
`;

const CalendarSkeleton = styled.div`
  border: 2px solid var(--gray-50);
  border-radius: 4px;
  padding: 10px;
  width: 100%;
`;

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 8px;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #f0f0f0;
`;

const SkeletonWeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 12px 0;
  text-align: center;
`;

const SkeletonWeekDay = styled.div`
  height: 20px;
  display: flex;
  justify-content: center;
`;

const SkeletonCell = styled.div`
  background: #fff;
  min-height: 110px;
  padding: 8px;
`;

type Props = {
  isLoading: boolean;
  hasLoadedOnce: boolean;
  selectedDate: Moment;
  setSelectedDate: Dispatch<SetStateAction<Moment>>;
  files: ObligationFile[];
  // eslint-disable-next-line no-unused-vars
  openModal: (file: ObligationFile) => void;
  filesModal: FilesModalProps;
  setFilesModal: Dispatch<SetStateAction<FilesModalProps>>;
};

export type FileStatus = 'expired' | 'visualized' | 'unviewed';

export const Calendar = ({
  isLoading,
  hasLoadedOnce,
  selectedDate,
  setSelectedDate,
  files,
  openModal,
  filesModal,
  setFilesModal,
}: Props) => {
  const openFilesModal = (
    currentFiles: ObligationFile[],
    status: FileStatus
  ) => {
    setFilesModal({
      isVisible: true,
      files: currentFiles,
      status,
    });
  };

  const closeFilesModal = () => {
    setFilesModal({
      isVisible: false,
      files: [],
      status: 'expired',
    });
  };

  const goToPreviousMonth = () => {
    setSelectedDate(selectedDate.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setSelectedDate(selectedDate.clone().add(1, 'month'));
  };

  const dateCellRender = (value: Moment) => {
    const currentFiles = files.filter(
      (item) => item.due_at === value.format('YYYY-MM-DD')
    );

    if (currentFiles.length > 0) {
      const { expiredFiles, unviewedFiles, visualizedFiles } =
        getFilteredFiles(currentFiles);

      return (
        <CalendarCellStyled>
          {expiredFiles.length > 0 && (
            <CellTagStyled
              color="var(--danger)"
              onClick={() => openFilesModal(expiredFiles, 'expired')}
            >
              <FileBadge $color="var(--danger)" count={expiredFiles.length} />
              Vencidos
            </CellTagStyled>
          )}
          {unviewedFiles.length > 0 && (
            <CellTagStyled
              color="var(--info)"
              onClick={() => openFilesModal(unviewedFiles, 'unviewed')}
            >
              <FileBadge $color="var(--info)" count={unviewedFiles.length} />
              Não baixados
            </CellTagStyled>
          )}
          {visualizedFiles.length > 0 && (
            <CellTagStyled
              color="var(--success)"
              onClick={() => openFilesModal(visualizedFiles, 'visualized')}
            >
              <FileBadge
                $color="var(--success)"
                count={visualizedFiles.length}
              />
              Baixados
            </CellTagStyled>
          )}
        </CalendarCellStyled>
      );
    }

    return <span />;
  };

  const renderHeader = () => {
    const monthYear = selectedDate.format('MMMM YYYY');
    return (
      <CalendarHeader>
        <MonthNavigator>
          <NavButton onClick={goToPreviousMonth} aria-label="Mês anterior">
            <LeftOutlined />
          </NavButton>
          <MonthYearLabel>{monthYear}</MonthYearLabel>
          <NavButton onClick={goToNextMonth} aria-label="Próximo mês">
            <RightOutlined />
          </NavButton>
        </MonthNavigator>
      </CalendarHeader>
    );
  };

  // Show skeleton when loading, with working navigation
  if (isLoading) {
    return (
      <>
        <CalendarSkeleton>
          <SkeletonHeader>
            <MonthNavigator>
              <NavButton onClick={goToPreviousMonth} aria-label="Mês anterior">
                <LeftOutlined />
              </NavButton>
              <MonthYearLabel>{selectedDate.format('MMMM YYYY')}</MonthYearLabel>
              <NavButton onClick={goToNextMonth} aria-label="Próximo mês">
                <RightOutlined />
              </NavButton>
            </MonthNavigator>
          </SkeletonHeader>
          <SkeletonWeekHeader>
            {[...Array(7)].map((_, i) => (
              <SkeletonWeekDay key={i}>
                <Skeleton.Button active size="small" style={{ width: 30, height: 16 }} />
              </SkeletonWeekDay>
            ))}
          </SkeletonWeekHeader>
          <SkeletonGrid>
            {[...Array(35)].map((_, i) => (
              <SkeletonCell key={i}>
                <Skeleton.Button active size="small" style={{ width: 20, height: 16, marginBottom: 8 }} />
                {i % 5 === 0 && (
                  <Skeleton.Button active size="small" style={{ width: '90%', height: 24, marginTop: 8 }} />
                )}
                {i % 7 === 2 && (
                  <Skeleton.Button active size="small" style={{ width: '90%', height: 24, marginTop: 8 }} />
                )}
              </SkeletonCell>
            ))}
          </SkeletonGrid>
        </CalendarSkeleton>
        <FilesModal
          isVisible={filesModal.isVisible}
          files={filesModal.files}
          status={filesModal.status}
          onClose={closeFilesModal}
          openModal={openModal}
        />
      </>
    );
  }

  // After initial load, show calendar without loading indicator
  return (
    <>
      <CalendarWrapper>
        <CalendarStyled
          $isLoading={false}
          dateCellRender={dateCellRender}
          mode="month"
          value={selectedDate}
          headerRender={renderHeader}
          onPanelChange={(date) => setSelectedDate(date)}
          disabledDate={(currentDate) =>
            currentDate.month() !== selectedDate.month()
          }
        />
      </CalendarWrapper>
      <FilesModal
        isVisible={filesModal.isVisible}
        files={filesModal.files}
        status={filesModal.status}
        onClose={closeFilesModal}
        openModal={openModal}
      />
    </>
  );
};
